/*
 * <hero-slider> — behaviour + accessibility for the CSS hero carousel.
 * CSS owns all layout, snapping and visuals. This module only:
 *   - builds a seamless wrap-around loop by cloning the first and last slides,
 *     so every slide (including the ends) shows both neighbours peeking and
 *     autoplay wraps from the last slide back to the first without rewinding;
 *   - auto-advances by listening to the active indicator's fill `animationend`,
 *     so the visible fill and the timer stay perfectly in sync;
 *   - keeps the active indicator in sync with manual swipe / scroll;
 *   - starts with the first slide centred and keeps the active slide centred;
 *   - brings a slide to the centre when keyboard focus moves into it, so a
 *     focused link is never left in a peeking or off-screen slide;
 *   - pauses on keyboard focus and when the tab is hidden, plus an optional
 *     pause/play control (WCAG 2.2.2). No hover pause — intentional, by design;
 *   - disables autoplay entirely under prefers-reduced-motion.
 * The clones are aria-hidden and taken out of the tab order, so assistive tech
 * and keyboard users only ever meet the real slides.
 *
 * Mouse drag comes from the shared assets/carousel-drag.js (touch already
 * swipes natively). While a drag is in progress autoplay is held and this
 * module's own settle logic stands down, so the timer can't advance the slide
 * out from under the cursor and a pause mid-drag can't trigger a clone hop.
 * Guarded so a repeated section only defines the element once.
 */
// Bare name, resolved by the import map in layout/theme.liquid to the
// versioned asset URL (a relative './' path would bypass cache-busting).
import { enableDrag } from 'carousel-drag';

if (!customElements.get('hero-slider')) {
  const reduce = window.matchMedia('(prefers-reduced-motion: reduce)');

  class HeroSlider extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('[data-hs-track]');
      if (!this.track) return;

      // Fresh state on every (re)connect — drop any clones from a prior run.
      this.track.querySelectorAll('[data-hs-clone]').forEach((c) => c.remove());
      const realCells = Array.from(this.track.children);
      this.n = realCells.length;
      if (this.n === 0) return;

      this.dots = Array.from(this.querySelectorAll('[data-hs-dot]'));
      this.toggleBtn = this.querySelector('[data-hs-toggle]');

      this.autoplay = this.dataset.autoplay === 'true' && this.n > 1;
      this.loop = this.n > 1;
      this.focused = false;
      this.userPaused = false;
      this.programmatic = false;

      this.classList.toggle('hero-slider--single', this.n === 1);

      // Label the real slides for assistive tech ("N of M").
      realCells.forEach((cell, i) => {
        const slide = cell.querySelector('[data-hs-slide]');
        if (slide) slide.setAttribute('aria-label', `${i + 1} of ${this.n}`);
      });

      // Wrap-around: clone the last slide before the first, and the first after
      // the last, so both neighbours always peek and the loop is seamless.
      if (this.loop) {
        this.track.insertBefore(this.makeClone(realCells[this.n - 1]), realCells[0]);
        this.track.appendChild(this.makeClone(realCells[0]));
      }

      this.cells = Array.from(this.track.children);
      this.startCell = this.loop ? 1 : 0; // index of the first *real* slide
      this.cellIndex = this.startCell;

      // Listeners on this element and its own controls are added once: the
      // theme editor moves nodes, which runs this again on the same buttons,
      // and a second listener would toggle pause twice (a dead button) and
      // advance two slides at the end of every fill.
      const wire = !this.wired;
      this.wired = true;

      if (wire) {
        this.dots.forEach((dot, i) => {
          dot.addEventListener('click', () => this.goToLogical(i));
          const fill = dot.querySelector('[data-hs-progress]');
          if (fill) {
            fill.addEventListener('animationend', () => {
              if (dot.getAttribute('aria-current') === 'true' && this.isPlaying()) this.next();
            });
          }
        });
      }

      this.onScroll = this.onScroll.bind(this);
      this.onSettleEvent = () => this.onSettle();
      this.track.addEventListener('scroll', this.onScroll, { passive: true });
      this.track.addEventListener('scrollend', this.onSettleEvent);

      if (wire) {
        this.addEventListener('focusin', (event) => {
          this.setFocused(true);
          this.followFocus(event.target);
        });
        this.addEventListener('focusout', (event) => {
          if (!this.contains(event.relatedTarget)) this.setFocused(false);
        });
      }

      this.onVisibility = () => this.applyPlayState();
      document.addEventListener('visibilitychange', this.onVisibility);

      if (wire && this.toggleBtn) {
        this.toggleBtn.addEventListener('click', () => {
          this.userPaused = !this.userPaused;
          this.updateToggle();
          this.applyPlayState();
        });
      }

      this.onReduceChange = () => this.refreshAutoplay();
      reduce.addEventListener('change', this.onReduceChange);

      this.dragging = false;
      if (this.n > 1) {
        this.drag = enableDrag(this.track, {
          onStart: () => {
            this.dragging = true;
            this.programmatic = false; // a drag is the visitor's choice, not ours
            this.applyPlayState();
          },
          // Settle explicitly once the drag has come to rest. Relying on this
          // element's own scrollend listener would race the drag module's —
          // whichever fired first could still see the drag as in progress.
          onEnd: () => {
            this.dragging = false;
            this.applyPlayState();
            this.onSettle();
            // A drag that wrapped onto a clone hops to its real twin without
            // touching the dots, so derive the active dot from where it
            // actually settled rather than from the last scroll frame.
            this.highlight(this.cellIndex);
          },
        });
      }

      this.refreshAutoplay();

      // Centre the first real slide on load. A clone precedes it, so scroll
      // position 0 is not centred — this is required, not just a safety net.
      requestAnimationFrame(() => this.jumpToCell(this.startCell));
    }

    disconnectedCallback() {
      document.removeEventListener('visibilitychange', this.onVisibility);
      reduce.removeEventListener('change', this.onReduceChange);
      this.track.removeEventListener('scroll', this.onScroll);
      this.track.removeEventListener('scrollend', this.onSettleEvent);
      cancelAnimationFrame(this.scrollRaf);
      cancelAnimationFrame(this.focusRaf);
      clearTimeout(this.settleTimer);
      if (this.drag) this.drag.destroy();
    }

    makeClone(cell) {
      const clone = cell.cloneNode(true);
      clone.setAttribute('data-hs-clone', '');
      clone.setAttribute('aria-hidden', 'true');
      clone.removeAttribute('id');
      // Keep clones out of the tab order and the theme editor's block model.
      Array.from(clone.attributes).forEach((a) => {
        if (a.name.startsWith('data-shopify')) clone.removeAttribute(a.name);
      });
      clone.querySelectorAll('a, button, [tabindex]').forEach((el) => el.setAttribute('tabindex', '-1'));
      const slide = clone.querySelector('[data-hs-slide]');
      if (slide) {
        slide.removeAttribute('data-hs-slide');
        slide.removeAttribute('aria-label');
      }
      return clone;
    }

    // Dot (logical) index for a given cell index.
    logicalIndex(cellIndex) {
      if (!this.loop) return cellIndex;
      if (cellIndex === 0) return this.n - 1; // leading clone = last real slide
      if (cellIndex === this.n + 1) return 0; // trailing clone = first real slide
      return cellIndex - 1;
    }

    refreshAutoplay() {
      const on = this.autoplay && !reduce.matches;
      this.classList.toggle('hero-slider--autoplay', on);
      if (this.toggleBtn) this.toggleBtn.hidden = !on;
      this.updateToggle();
      this.applyPlayState();
      this.setActive(this.cellIndex); // (re)start the fill to match the new state
    }

    isPlaying() {
      return (
        this.autoplay &&
        !reduce.matches &&
        !this.focused &&
        !this.userPaused &&
        !this.dragging &&
        !document.hidden
      );
    }

    applyPlayState() {
      this.classList.toggle('is-paused', !this.isPlaying());
    }

    setFocused(state) {
      this.focused = state;
      this.applyPlayState();
    }

    // Tab into a slide that isn't the current one — a neighbour peeking at
    // the side, or one off screen — brings it to the centre, as its dot
    // would. A frame later, so this comes after the browser's own scrolling
    // of the focused link into view and wins. Keyboard focus only: a click on
    // a peeking slide's link is left to go where it goes.
    followFocus(target) {
      if (!target.matches(':focus-visible')) return;
      const cell = target.closest('[data-hs-track] > *');
      if (!cell || cell.hasAttribute('data-hs-clone')) return;
      const index = this.cells.indexOf(cell);
      if (index === -1 || index === this.cellIndex) return;
      cancelAnimationFrame(this.focusRaf);
      this.focusRaf = requestAnimationFrame(() => this.scrollToCell(index));
    }

    updateToggle() {
      if (!this.toggleBtn) return;
      const playing = !this.userPaused;
      this.toggleBtn.dataset.state = playing ? 'playing' : 'paused';
      this.toggleBtn.setAttribute('aria-label', playing ? 'Pause slideshow' : 'Play slideshow');
    }

    // Highlight the dot for a cell (no fill restart) — used live while scrolling.
    highlight(cellIndex) {
      const logical = this.logicalIndex(cellIndex);
      this.dots.forEach((dot, di) => {
        if (di === logical) dot.setAttribute('aria-current', 'true');
        else dot.removeAttribute('aria-current');
      });
    }

    // Adopt a cell as active: highlight its dot and restart the autoplay fill.
    setActive(cellIndex) {
      this.cellIndex = cellIndex;
      this.highlight(cellIndex);
      const activeDot = this.dots[this.logicalIndex(cellIndex)];
      const fill = activeDot && activeDot.querySelector('[data-hs-progress]');
      if (fill) {
        fill.style.animation = 'none';
        void fill.offsetWidth; // reflow to replay the fill from 0
        fill.style.animation = '';
      }
    }

    center(cellIndex) {
      const cell = this.cells[cellIndex];
      if (!cell) return 0;
      return cell.offsetLeft - (this.track.clientWidth - cell.clientWidth) / 2;
    }

    // Smooth-scroll a cell to centre and adopt it as the active slide.
    scrollToCell(cellIndex) {
      this.programmatic = true;
      this.setActive(cellIndex);
      this.track.scrollTo({ left: this.center(cellIndex), behavior: reduce.matches ? 'auto' : 'smooth' });
    }

    // Instant hop (no fill restart) — used to jump from a clone to its real
    // twin so the wrap-around is invisible.
    jumpToCell(cellIndex) {
      this.programmatic = true;
      this.cellIndex = cellIndex;
      this.track.scrollTo({ left: this.center(cellIndex), behavior: 'auto' });
    }

    next() {
      this.scrollToCell(this.cellIndex + 1);
    }

    goToLogical(logical) {
      this.scrollToCell(this.loop ? logical + 1 : logical);
    }

    nearestCell() {
      const viewportCenter = this.track.scrollLeft + this.track.clientWidth / 2;
      let nearest = 0;
      let smallest = Infinity;
      this.cells.forEach((cell, i) => {
        const cellCenter = cell.offsetLeft + cell.clientWidth / 2;
        const distance = Math.abs(cellCenter - viewportCenter);
        if (distance < smallest) {
          smallest = distance;
          nearest = i;
        }
      });
      return nearest;
    }

    onScroll() {
      cancelAnimationFrame(this.scrollRaf);
      this.scrollRaf = requestAnimationFrame(() => {
        if (!this.programmatic) this.highlight(this.nearestCell());
      });
      // Debounced fallback for browsers that don't fire `scrollend`.
      clearTimeout(this.settleTimer);
      this.settleTimer = setTimeout(() => this.onSettle(), 160);
    }

    onSettle() {
      clearTimeout(this.settleTimer);
      // A drag owns the track until it has come to rest; its onEnd settles.
      if (this.dragging) return;
      const nearest = this.nearestCell();
      const wasProgrammatic = this.programmatic;
      this.programmatic = false;

      // Seamless wrap: if scrolling settled on a clone, hop to its real twin.
      if (this.loop && (nearest === 0 || nearest === this.n + 1)) {
        this.jumpToCell(nearest === this.n + 1 ? 1 : this.n);
        return;
      }
      // A manual swipe that landed on a real slide adopts it (restarts timer).
      if (!wasProgrammatic && nearest !== this.cellIndex) this.setActive(nearest);
      else this.cellIndex = nearest;
    }
  }

  customElements.define('hero-slider', HeroSlider);
}
